import { t } from '@lingui/core/macro'
import type { SystemDocumentEntry } from 'features/knowledgeDocumentViewer/types/systemDocumentEntries'

export function allDocumentsHaveRevisionCodes(
  documents: SystemDocumentEntry[]
): boolean {
  return documents.every(
    (doc) =>
      doc.entry.data.revision_code !== undefined &&
      doc.entry.data.revision_code !== null &&
      doc.entry.data.revision_code !== ''
  )
}

/**
 * Compares two revision codes in ascending order.
 * Letters sort before numbers (A, B, C, 0, 1, 2, 3).
 * Letters are compared alphabetically; numbers are compared numerically.
 */
export function compareRevisionCodes(revA: string, revB: string): number {
  const isANumber = /^\d+$/.test(revA)
  const isBNumber = /^\d+$/.test(revB)

  if (isANumber !== isBNumber) return isANumber ? 1 : -1

  return isANumber ? parseInt(revA) - parseInt(revB) : revA.localeCompare(revB)
}

export function getLatestDocumentByRevision(
  documents: SystemDocumentEntry[]
): SystemDocumentEntry | undefined {
  if (documents.length === 0) return undefined
  const sortedByRevision = [...documents].sort((a, b) => {
    const revA = a.entry.data.revision_code ?? ''
    const revB = b.entry.data.revision_code ?? ''
    return -compareRevisionCodes(revA, revB)
  })
  return sortedByRevision[0]
}

export type RevisionStamp = {
  key: string
  code: string | null | undefined
  date: string | null | undefined
}

/** One row of a revision menu, independent of the record it came from. */
export type RevisionMenuItemView = RevisionStamp & {
  isCurrent: boolean
  isSelectable: boolean
}

type NormalizedRevisionStamp = Record<'code' | 'date', string>

const REVISION_STAMP_FIELDS: readonly (keyof NormalizedRevisionStamp)[] = [
  'code',
  'date',
]

function normalizeRevisionStamp(stamp: RevisionStamp): NormalizedRevisionStamp {
  return {
    code: stamp.code?.trim().toUpperCase() ?? '',
    date: stamp.date?.trim() ?? '',
  }
}

function anyKnownFieldDiffers(
  a: NormalizedRevisionStamp,
  b: NormalizedRevisionStamp
): boolean {
  return REVISION_STAMP_FIELDS.some(
    (field) => a[field] !== '' && b[field] !== '' && a[field] !== b[field]
  )
}

function anyKnownFieldMatches(
  a: NormalizedRevisionStamp,
  b: NormalizedRevisionStamp
): boolean {
  return REVISION_STAMP_FIELDS.some(
    (field) => a[field] !== '' && a[field] === b[field]
  )
}

function isSameRevisionStamp(
  a: NormalizedRevisionStamp,
  b: NormalizedRevisionStamp
): boolean {
  return anyKnownFieldMatches(a, b) && !anyKnownFieldDiffers(a, b)
}

/**
 * Whether the latest-first `revisions` lead with a revision newer than the one
 * keyed `currentKey` (the first revision when `currentKey` is not found).
 * The latest is not newer when it shares a known code or date with the current
 * one and no field known on both sides differs; any differing known field, or
 * no shared known field, makes it newer.
 */
export function hasNewerRevision(
  revisions: readonly RevisionStamp[],
  currentKey: string | undefined
): boolean {
  const latest = revisions[0]
  const current =
    revisions.find((revision) => revision.key === currentKey) ?? latest
  if (!latest || !current || current.key === latest.key) return false
  return !isSameRevisionStamp(
    normalizeRevisionStamp(current),
    normalizeRevisionStamp(latest)
  )
}

export function formatRevisionLabel(code: string | null | undefined): string {
  const revisionCode = code?.trim()
  return revisionCode ? t`Rev ${revisionCode}` : t`Rev unknown`
}
